import React, { useEffect, useState } from 'react';
import { UserPlus, Users, Trash2, ShieldCheck, ArrowLeft, Crown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import MobileLayout from '../components/layout/MobileLayout';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const ROLE_COLORS = {
    owner: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30',
    manager: 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30',
    cashier: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'
};

const Team = () => {
    const { authAxios } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState({ owner: null, members: [], pending_invites: [], current_user_role: 'owner' });
    const [loading, setLoading] = useState(true);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [phone, setPhone] = useState('+91');
    const [name, setName] = useState('');
    const [role, setRole] = useState('cashier');
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const res = await authAxios.get('/team/members');
            setData(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load team');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const canManage = data.current_user_role === 'owner';

    const submitInvite = async () => {
        setSaving(true);
        try {
            await authAxios.post('/team/invite', { phone, name: name || null, role });
            toast.success('Invite sent');
            setInviteOpen(false);
            setPhone('+91'); setName(''); setRole('cashier');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to invite');
        } finally {
            setSaving(false);
        }
    };

    const changeRole = async (memberId, newRole) => {
        try {
            await authAxios.patch(`/team/members/${memberId}/role`, { role: newRole });
            toast.success('Role updated');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
    };

    const removeMember = async (memberId) => {
        if (!window.confirm('Remove this team member? They will lose access immediately.')) return;
        try {
            await authAxios.delete(`/team/members/${memberId}`);
            toast.success('Member removed');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
    };

    const cancelInvite = async (inviteId) => {
        try {
            await authAxios.delete(`/team/invites/${inviteId}`);
            toast.success('Invite cancelled');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
    };

    return (
        <MobileLayout>
            <div className="space-y-6" data-testid="team-page">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} data-testid="team-back-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <h1 className="text-2xl font-bold font-heading flex-1">Team</h1>
                    {canManage && (
                        <Button onClick={() => setInviteOpen(true)} data-testid="invite-member-btn" size="sm">
                            <UserPlus className="w-4 h-4 mr-1" />
                            Invite
                        </Button>
                    )}
                </div>

                {loading ? (
                    <Card><CardContent className="p-6 text-center text-muted-foreground">Loading team…</CardContent></Card>
                ) : (
                    <>
                        {/* Owner Card */}
                        {data.owner && (
                            <Card data-testid="team-owner-card">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Crown className="w-5 h-5 text-amber-500" />
                                        Owner
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="p-4 pt-0">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-semibold">{data.owner.name}</p>
                                            <p className="text-sm text-muted-foreground font-number">{data.owner.phone}</p>
                                        </div>
                                        <Badge variant="outline" className={ROLE_COLORS.owner}>Owner</Badge>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Members */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Users className="w-5 h-5" />
                                    Members ({data.members.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-4 pt-0 space-y-3">
                                {data.members.length === 0 && (
                                    <p className="text-sm text-muted-foreground py-4 text-center">No team members yet. Invite someone to get started.</p>
                                )}
                                {data.members.map((m) => (
                                    <div key={m.id} className="flex items-center justify-between border rounded-lg p-3" data-testid={`team-member-${m.id}`}>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">{m.name}</p>
                                            <p className="text-xs text-muted-foreground font-number">{m.phone}</p>
                                        </div>
                                        {canManage ? (
                                            <div className="flex items-center gap-2">
                                                <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                                                    <SelectTrigger className="w-32 h-8" data-testid={`role-select-${m.id}`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="manager">Manager</SelectItem>
                                                        <SelectItem value="cashier">Cashier</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <Button variant="ghost" size="icon" onClick={() => removeMember(m.id)} data-testid={`remove-member-${m.id}`}>
                                                    <Trash2 className="w-4 h-4 text-destructive" />
                                                </Button>
                                            </div>
                                        ) : (
                                            <Badge variant="outline" className={ROLE_COLORS[m.role] || ''}>{m.role}</Badge>
                                        )}
                                    </div>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Pending invites */}
                        {data.pending_invites.length > 0 && (
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <ShieldCheck className="w-5 h-5" />
                                        Pending Invites ({data.pending_invites.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="p-4 pt-0 space-y-3">
                                    {data.pending_invites.map((inv) => (
                                        <div key={inv.id} className="flex items-center justify-between border border-dashed rounded-lg p-3" data-testid={`pending-invite-${inv.id}`}>
                                            <div>
                                                <p className="font-medium">{inv.name || 'Unnamed'}</p>
                                                <p className="text-xs text-muted-foreground font-number">{inv.phone}</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Badge variant="outline" className={ROLE_COLORS[inv.role] || ''}>{inv.role}</Badge>
                                                {canManage && (
                                                    <Button variant="ghost" size="icon" onClick={() => cancelInvite(inv.id)} data-testid={`cancel-invite-${inv.id}`}>
                                                        <Trash2 className="w-4 h-4 text-destructive" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>
                        )}
                    </>
                )}

                {/* Invite modal */}
                <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                    <DialogContent data-testid="invite-modal">
                        <DialogHeader>
                            <DialogTitle>Invite Team Member</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-2">
                            <div>
                                <label className="text-sm font-medium">Phone (with +91)</label>
                                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+919876543210" className="mt-1 font-number" data-testid="invite-phone-input" />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Name (optional)</label>
                                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ramesh" className="mt-1" data-testid="invite-name-input" />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Role</label>
                                <Select value={role} onValueChange={setRole}>
                                    <SelectTrigger className="mt-1" data-testid="invite-role-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="manager">Manager (view + edit)</SelectItem>
                                        <SelectItem value="cashier">Cashier (record sales)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
                            <Button onClick={submitInvite} disabled={saving} data-testid="submit-invite-btn">
                                {saving ? 'Inviting…' : 'Send Invite'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </MobileLayout>
    );
};

export default Team;
